import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, Clock, Users, Maximize2, Loader2, CalendarPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/utils';

const PRIORITY_DOT = {
  urgent: 'bg-red-500',
  normal: 'bg-blue-500',
  low: 'bg-slate-400',
};

export default function MeetingsWidget({ onExpand }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const data = await apiRequest('/smart-meetings/dashboard');
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const fmtTime = (iso) => iso ? iso.substring(11, 16) : '';

  const upcoming = dashboard?.upcoming || [];
  const pendingCount = dashboard?.pending_response_count || 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4 text-violet-600" />
          Upcoming Meetings
          {pendingCount > 0 && <Badge className="bg-amber-500 text-white text-[10px]">{pendingCount} pending</Badge>}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={() => navigate('/book-meeting')}>
          <Maximize2 className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
        ) : upcoming.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-slate-400">No upcoming meetings</p>
            <Button variant="ghost" size="sm" className="mt-2 text-violet-600"
              onClick={() => navigate('/book-meeting')}>
              <CalendarPlus className="h-4 w-4 mr-1" /> New Meeting
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {upcoming.slice(0, 4).map(m => (
              <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/book-meeting?id=${m.id}`)}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[m.priority] || 'bg-blue-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{m.title}</div>
                  <div className="text-xs text-slate-500">{fmtDate(m.proposed_time)} at {fmtTime(m.proposed_time)}</div>
                </div>
                <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">Confirmed</Badge>
              </div>
            ))}
            {upcoming.length > 4 && (
              <Button variant="ghost" size="sm" className="w-full text-violet-600" onClick={() => navigate('/book-meeting')}>
                View all {upcoming.length} meetings
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
